import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Switch, Typography, Slider, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import { useSignetStudio } from '../SignetStudioContext';
import SizePreview from './SizePreview';
import CircularRange from './CircularRange';
import { formatScaleValue, getValueLabelFormat } from '../utils/formatter';
import { getConfigSwitchLabel } from './ShapeAccordion';
const useStyles = makeStyles((theme) => ({
  shankSection: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  shankHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  shankName: {
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  sliderContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto 60px 1fr',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  sliderLabel: {
    whiteSpace: 'nowrap',
    minWidth: 60,
  },
  scaleBadge: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.75rem',
    fontWeight: 500,
    minWidth: 60,
    textAlign: 'center',
    justifySelf: 'end',
  },
  sliderWrapper: {
    width: '100%',
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  previewContainer: {
    marginTop: theme.spacing(2),
  },
  shadowContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  shadowLabel: {
    fontWeight: 500,
    alignSelf: 'center',
  },
  radioGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  radioOption: {
    marginRight: theme.spacing(1.5),
  },
  rotationContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  rotationLabel: {
    fontWeight: 500,
  },
}));

const ShankCard = ({ 
  size, 
  sizeConfig, 
  shape,
  shank,
  shapeEnabled, 
  shankConfig,
  onShankToggle,
  onScaleChange, 
  onPaddingChange,
  onShadowChange,
  onRotationChange 
}) => {
  const classes = useStyles();
  const { state } = useSignetStudio();
  const configSwitchLabel = getConfigSwitchLabel(state);

  return (
            <div key={shank.id} className={classes.shankSection}>
              <div className={classes.shankHeader}>
                <Switch
                  checked={shank.checked}
                  onChange={(e) => onShankToggle(shape?.id, size.id, shank.sku, e.target.checked)}
                  color="primary"
                  disabled={!size.checked || !shapeEnabled}
                />
                <Typography className={classes.shankName}>
                  {shank.name}
                </Typography>
              </div>

              <div className={classes.sliderContainer}>
                <Typography variant="body2" className={classes.sliderLabel}>
                  Scale
                </Typography>
                <span className={classes.scaleBadge}>
                  {formatScaleValue(shankConfig?.config[configSwitchLabel].scale || 0, !shank.checked || !size.checked || !shapeEnabled)}
                </span>
                <div className={classes.sliderWrapper}>
                  <Slider
                    value={shankConfig?.config[configSwitchLabel].scale || 0}
                    onChange={(_, value) => onScaleChange(shape?.id, size.id, shank.sku, value)}
                    min={-50}
                    max={50}
                    step={1}
                    disabled={!shank.checked || !size.checked || !shapeEnabled}
                    valueLabelDisplay="auto"
                    valueLabelFormat={getValueLabelFormat}
                  />
                </div>
              </div>

              <div className={classes.sliderContainer}>
                <Typography variant="body2" className={classes.sliderLabel}>
                  Padding
                </Typography>
                <span className={classes.scaleBadge}>
                  {formatScaleValue(shankConfig?.config[configSwitchLabel].padding || 0, !shank.checked || !size.checked || !shapeEnabled)}
                </span>
                <div className={classes.sliderWrapper}>
                  <Slider
                    value={shankConfig?.config[configSwitchLabel].padding || 0}
                    onChange={(_, value) => onPaddingChange(shape?.id, size.id, shank.sku, value)}
                    min={0}
                    max={50}
                    step={1}
                    disabled={!shank.checked || !size.checked || !shapeEnabled}
                    valueLabelDisplay="auto"
                    valueLabelFormat={getValueLabelFormat}
                  />
                </div>
              </div>

              <div className={classes.shadowContainer}>
                <Typography variant="body2" className={classes.shadowLabel}>
                  Shadow
                </Typography>
                <RadioGroup
                  value={shankConfig?.config[configSwitchLabel].depth || 'standard'}
                  onChange={(e) => onShadowChange(shape?.id, size.id, shank.sku, e.target.value)}
                  className={classes.radioGroup}
                  disabled={!shank.checked || !size.checked || !shapeEnabled}
                >
                  <FormControlLabel
                    value="shallow"
                    className={classes.radioOption}
                    control={<Radio size="small" />}
                    label="Shallow"
                    disabled={!shank.checked || !size.checked || !shapeEnabled}
                  />
                  <FormControlLabel
                    value="standard"
                    className={classes.radioOption}
                    control={<Radio size="small" />}
                    label="Standard"
                    disabled={!shank.checked || !size.checked || !shapeEnabled}
                  />
                  <FormControlLabel
                    value="deep"
                    className={classes.radioOption}
                    control={<Radio size="small" />}
                    label="Deep"
                    disabled={!shank.checked || !size.checked || !shapeEnabled}
                  />
                </RadioGroup>
              </div>

              <div className={classes.rotationContainer}>
                <Typography variant="body2" className={classes.rotationLabel}>
                  Rotation
                </Typography>
                <CircularRange
                  value={shankConfig?.config[configSwitchLabel].shape_rotation || 0}
                  onChange={(value) => onRotationChange(shape?.id, size.id, shank.sku, value)}
                  disabled={!shank.checked || !size.checked || !shapeEnabled}
                  size={72}
                  strokeWidth={4}
                />
              </div>

              <div className={classes.previewContainer}>
                {state.svgFile ? (
                  <SizePreview
                    svgFile={state.svgFile}
                    shapeConfig={state.shapeConfigs.find((config) => config.shape_sku === shape?.sku)}
                    sizeConfig={sizeConfig}
                    shank={shankConfig}
                    disabled={!shank.checked || !size.checked || !shapeEnabled}
                  />
                ) : null}
              </div>
            </div>
  );
};

export default ShankCard;
